// src/hooks/useKDSOrders.js
import { useState, useEffect, useCallback } from 'react';
import apiClient from '../services/api/client';
import ENDPOINTS from '../services/api/endpoints';

// Time threshold for delayed orders (in seconds)
const DELAYED_THRESHOLD = 10 * 60; // 10 minutes

/**
 * Hook for fetching and managing KDS orders
 * Uses optimized KDS API endpoint for better performance
 * @param {string|string[]} stationIds - Single station ID, array of station IDs, or ['all']
 * @param {string} statusFilter - Status filter: 'active', 'new', 'cooking', 'ready'
 */
export function useKDSOrders(stationIds = ['all'], statusFilter = 'active') {
  const [orders, setOrders] = useState([]);
  const [stations, setStations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Fetch active orders using optimized KDS endpoint
  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Build query params for KDS API
      const params = new URLSearchParams();

      // Add status filters
      if (statusFilter === 'active') {
        params.append('status', 'SENT');
        params.append('status', 'PREPARING');
        params.append('status', 'READY');
      } else if (statusFilter === 'new') {
        params.append('status', 'SENT');
      } else if (statusFilter === 'cooking') {
        params.append('status', 'PREPARING');
      } else if (statusFilter === 'ready') {
        params.append('status', 'READY');
      }

      // Add station filter if selected (support multiple stations)
      const stationArray = Array.isArray(stationIds) ? stationIds : [stationIds];
      const hasAllSelected = stationArray.includes('all') || stationArray.length === 0;

      if (!hasAllSelected) {
        stationArray.forEach(id => {
          params.append('stationId', id);
        });
      }

      // Fetch orders using optimized KDS endpoint
      const ordersUrl = `${ENDPOINTS.KDS.GET_ORDERS}?${params.toString()}`;
      const response = await apiClient.get(ordersUrl);

      let ordersData = response?.data || response || [];
      if (!Array.isArray(ordersData)) {
        ordersData = ordersData?.data || ordersData?.content || [];
      }

      // Sort by creation time (oldest first) - backend already sorts but ensure client-side
      ordersData.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

      setOrders(ordersData);
      setLastUpdated(new Date().toISOString());
    } catch (err) {
      console.error('Failed to fetch KDS orders:', err);
      setError(err?.response?.data?.message || 'Failed to load orders');
    } finally {
      setLoading(false);
    }
  }, [stationIds, statusFilter]);

  // Fetch kitchen stations
  const fetchStations = useCallback(async () => {
    try {
      const response = await apiClient.get(ENDPOINTS.KITCHEN_STATIONS?.BASE || '/api/admin/kitchen-stations');
      let stationsData = response?.data?.data || response?.data || response || [];
      if (!Array.isArray(stationsData)) {
        stationsData = [];
      }
      // Filter only active stations
      stationsData = stationsData.filter(s => s.isActive !== false);
      setStations(stationsData);
    } catch (err) {
      console.warn('Failed to fetch kitchen stations:', err);
      setStations([]);
    }
  }, []);

  // Update order status using optimized KDS endpoint
  const updateOrderStatus = useCallback(async (orderId, newStatus) => {
    try {
      await apiClient.put(`${ENDPOINTS.KDS.UPDATE_STATUS(orderId)}?status=${newStatus}`);

      // Update local state optimistically
      setOrders(prev => prev.map(order =>
        order.id === orderId
          ? { ...order, orderStatus: newStatus, status: newStatus }
          : order
      ).filter(order => {
        // Remove served orders from active list
        if (statusFilter === 'active' && (order.orderStatus === 'SERVED' || order.status === 'SERVED')) {
          return false;
        }
        return true;
      }));

      setLastUpdated(new Date().toISOString());
      return true;
    } catch (err) {
      console.error('Failed to update order status:', err);
      // Refresh orders to get accurate state
      fetchOrders();
      throw err;
    }
  }, [fetchOrders, statusFilter]);

  // Update item status (PENDING, PREPARING, READY, SERVED)
  const updateItemStatus = useCallback(async (orderId, itemId, newStatus) => {
    try {
      const response = await apiClient.put(
        `${ENDPOINTS.KDS.UPDATE_ITEM_STATUS(orderId, itemId)}?status=${newStatus}`
      );

      // Update local state with response data
      const updatedOrder = response?.data || response;
      if (updatedOrder && updatedOrder.id) {
        setOrders(prev => prev.map(order =>
          order.id === orderId ? updatedOrder : order
        ).filter(order => {
          // Remove served orders from active list
          if (statusFilter === 'active' && (order.orderStatus === 'SERVED' || order.status === 'SERVED')) {
            return false;
          }
          return true;
        }));
      }

      setLastUpdated(new Date().toISOString());
      return updatedOrder;
    } catch (err) {
      console.error('Failed to update item status:', err);
      fetchOrders();
      throw err;
    }
  }, [fetchOrders, statusFilter]);

  // Calculate stats
  const stats = {
    new: orders.filter(o => (o.orderStatus || o.status) === 'SENT').length,
    cooking: orders.filter(o => (o.orderStatus || o.status) === 'PREPARING').length,
    ready: orders.filter(o => (o.orderStatus || o.status) === 'READY').length,
    delayed: orders.filter(o => {
      const elapsed = (Date.now() - new Date(o.createdAt).getTime()) / 1000;
      const status = o.orderStatus || o.status;
      return elapsed > DELAYED_THRESHOLD && status !== 'READY' && status !== 'SERVED';
    }).length,
    total: orders.length
  };

  // Initial fetch
  useEffect(() => {
    fetchOrders();
    fetchStations();
  }, [fetchOrders, fetchStations]);

  return {
    orders,
    stations,
    loading,
    error,
    stats,
    lastUpdated,
    refreshOrders: fetchOrders,
    updateOrderStatus,
    updateItemStatus
  };
}

export default useKDSOrders;
